/**
 * @module support/shared
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ /** */
import { Injectable } from '@angular/core';
import { Validators, ValidatorFn, UntypedFormControl, AbstractControl } from '@angular/forms';
import { CommonApiService } from '@support/shared/services/common-api/common-api.service';
import {
  AddressFormFieldMapping,
  InputItemData,
} from '@support/shared/widgets/interfaces/address-form.interface';

@Injectable(
  { providedIn: 'root' }
)
export class FormCreationService {
  /**
   * form control name mapping to aem field to fetch validation
   */
  public addressFormValidationMapping: AddressFormFieldMapping;

  constructor(private commonApiService: CommonApiService) {
    const fieldValidationsConstants = this.commonApiService.getFieldValidationConstants();
    this.addressFormValidationMapping = {
      firstName: fieldValidationsConstants.FIRST_NAME,
      middleName: fieldValidationsConstants.MIDDLE_INITIAL,
      lastName: fieldValidationsConstants.LAST_NAME,
      address1: fieldValidationsConstants.ADDRESS_LINE1,
      address2: fieldValidationsConstants.ADDRESS_LINE2,
      zipCode: fieldValidationsConstants.ZIP_CODE,
      city: fieldValidationsConstants.CITY,
      state: '',
      urbanisation: fieldValidationsConstants.URBANISATION,
      contactNumber: fieldValidationsConstants.PHONE_NUMBER,
      emailAddress: fieldValidationsConstants.EMAIL_ADDRESS,
    };
  }

  /**
   * method to get dynamic form control for address component
   * @param name: name of control
   * @param inputData: data needed to create control
   */
  public getAddressFormControl(name: string, inputData: InputItemData): Object {
    return this.addressFormValidationMapping[name] === '' 
      ? [inputData?.value, inputData?.required ? Validators.required : []]
      : this.getFormControl(this.addressFormValidationMapping[name], inputData?.value, inputData?.required);
  }
  /**
   * method to get control
   * @param fieldName: name of field in aem validations json
   * @param controlValue: value of form control to be prepopulated
   * @param required: if the form control is mandatory
   * @returns void
   */
  public getFormControl(fieldName: string, controlValue: string, required: boolean): Object {
    return [
      { value: controlValue, disabled: false },
      this.getFormControlValidator(fieldName, required),
    ];
  }
  /**
   * method to get validations for a control
   * @param validatorKey: name of field in aem validations json
   * @param required: if the form control is mandatory
   * @returns  ValidatorFn[]
   */
  public getFormControlValidator(validatorKey: string, required: boolean): ValidatorFn[] {
    const validationObject = this.commonApiService.getFieldValidation(validatorKey);
    const validators: ValidatorFn[] = [];
    if (required) validators.push(Validators.required);
    if (validationObject) {
      if (validationObject?.regex) validators.push(Validators.pattern(validationObject.regex));
      if (validationObject?.fieldAttributes?.min) validators.push(Validators.minLength(validationObject?.fieldAttributes?.min));
      if (validationObject?.fieldAttributes?.max) validators.push(Validators.maxLength(validationObject?.fieldAttributes?.max));
    }
    return validators;
  }
  /**
   * method to get control
   * @param fieldName: name of field in aem validations json
   * @param controlValue: value of form control to be prepopulated
   * @param required: if the form control is mandatory
   * @returns void
   */
  public getAbstractFormControl(
    fieldName: string,
    controlValue: string,
    required: boolean
  ): AbstractControl {
    return new UntypedFormControl(
      { value: controlValue, disabled: false },
      this.getFormControlValidator(fieldName, required)
    );
  }
}
